import { jsonReplacer } from "./format";

/**
 * Render one DuckDB result value for a CSV cell.
 *
 * Renders for tools like Excel or pandas, where `formatCellValue` renders for
 * the screen: missing values become an empty field instead of the text `NULL`,
 * and timestamps are UTC ISO-8601 so a CSV opened in another time zone still
 * means the same instant.
 */
export function csvCellValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value, jsonReplacer);
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") return value;

  // Every type a DuckDB row can hold is handled above.
  return "";
}

/**
 * Escape one CSV field per RFC 4180: quote it if it contains a comma, a
 * double quote, or a line break, doubling any double quotes inside.
 */
function csvField(value: string): string {
  if (!/[",\r\n]/.test(value)) return value;
  return `"${value.replaceAll('"', '""')}"`;
}

/**
 * Serialize a query result as an RFC 4180 CSV, including a header row.
 *
 * Uses `\r\n` line endings, as RFC 4180 specifies and Excel expects, and does
 * not append a trailing line ending after the last row.
 */
export function toCsv(
  columns: string[],
  rows: Record<string, unknown>[]
): string {
  const header = columns.map(csvField).join(",");
  const body = rows.map((row) =>
    columns.map((column) => csvField(csvCellValue(row[column]))).join(",")
  );

  return [header, ...body].join("\r\n");
}

/**
 * Trigger a browser download of `csv` as a file named `filename`.
 *
 * A UTF-8 byte-order mark is prepended to the blob, not to `toCsv`'s output,
 * because Excel otherwise guesses a different encoding and mangles non-ASCII
 * characters.
 */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}
